import React, { useState, useEffect } from "react";
import styles from "./chat-buddy-msg.module.less";
import {
    ActionParamAuth,
    BuddyMsgProps,
    Button,
    ChatAuthBtns,
    ChatMsg,
    ChatOTPBtns,
    DetectIntentData, FormButtonParams, FormButtonValue
} from "../../types/chat-msg-types";
import ReactHtmlParser from 'react-html-parser';
import ChatTime from "../chat-time";
import { CONFIG, USER_MSG } from "../../common/config";
import { addChatMsgAction, downloadByButtonAction, reportUserAction, sendMessageAction } from "../../actions/chat-msg-actions";
import { useDispatch } from "react-redux";
import ChatAuthForm from "../chat-buddy-auth-form/chat-buddy-auth-form";
import ChatOtp from "../../components/chat-otp/chat-otp";
import { getBlankOrSelf } from "../../utils/utils";
import ChatFeedback from "../../components/chat-feedback/chat-feedback";
import unfoldImg from "@/assets/Chevron-down.svg";
import foldImg from "@/assets/Chevron-up.svg";
import NewChatFeedback from "../chat-feedback/chat-feedback-new";

const BLOCKED_HTML_TAGS = new Set(["script", "style", "iframe", "object", "embed", "link", "meta", "base"]);
const SAFE_FORM_METHODS = new Set(["GET", "POST"]);
const SAFE_FORM_TARGETS = new Set(["_self", "_blank", "_top", "_parent"]);


const ALLOWED_URL_PATTERN =
  /^(\/|#|https?:|mailto:|tel:)/i;

const isSafeUrl = (url?: string): boolean => {
    if (!url) {
    return false;
    }

    const trimmedUrl = url.trim();

    return ALLOWED_URL_PATTERN.test(trimmedUrl);
};


const sanitizeUrl = (url?: string, fallback = "#"): string => {
    if (!isSafeUrl(url)) {
        return fallback;
    }
    return url.trim();
};

const sanitizeHtml = (html?: string): string => {
    if (!html) {
        return "";
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    BLOCKED_HTML_TAGS.forEach((tag) => {
        doc.querySelectorAll(tag).forEach((node) => node.remove());
    });

    doc.querySelectorAll("*").forEach((element) => {
        Array.from(element.attributes).forEach((attr) => {
            const attrName = attr.name.toLowerCase();
            if (/^on/i.test(attrName)) {
                element.removeAttribute(attr.name);
                return;
            }

            if ((attrName === "href" || attrName === "src") && !isSafeUrl(attr.value)) {
                if (attrName === "href") {
                    element.setAttribute(attr.name, "#");
                } else {
                    element.removeAttribute(attr.name);
                }
            }
        });
    });

    return doc.body.innerHTML;
};

const sanitizeFormMethod = (method?: string): string => {
    const normalizedMethod = (method || "GET").toUpperCase();
    return SAFE_FORM_METHODS.has(normalizedMethod) ? normalizedMethod : "GET";
};

const sanitizeFormTarget = (target?: string): string => {
    return SAFE_FORM_TARGETS.has(target || "") ? target : "_self";
};

const safeJsonParse = <T,>(raw: string | undefined, fallback: T): T => {
    if (!raw) {
        return fallback;
    }
    try {
        return JSON.parse(raw) as T;
    } catch (error) {
        if (error instanceof SyntaxError) {
            return fallback;
        }
        throw error;
    }
};

const sanitizeTargetOrigin = (origin?: string): string => {
    if (!origin || origin === "*") {
        return globalThis.location.origin;
    }

    try {
        const parsed = new URL(origin);
        if (parsed.protocol === "https:" || parsed.protocol === "http:") {
            return parsed.origin;
        }
    } catch (error) {
        if (!(error instanceof TypeError)) {
            throw error;
        }
    }

    return globalThis.location.origin;
};

const htmlParserOptions = {
    transform: (node: any) => {
        if (node?.type !== "tag") {
            return;
        }

        const tagName = (node.name || "").toLowerCase();
        if (BLOCKED_HTML_TAGS.has(tagName)) {
            return <></>;
        }

        const attribs = node.attribs || {};
        Object.keys(attribs).forEach((attr) => {
            if (/^on/i.test(attr)) {
                delete attribs[attr];
            }
        });

        if (attribs.href) {
            attribs.href = sanitizeUrl(attribs.href, "#");
        }
        if (attribs.src) {
            attribs.src = sanitizeUrl(attribs.src, "");
        }
    }
};

interface RichResponseProps {
    richResponse: any;
    actionParam: { type?: string; form?: ActionParamAuth[]; btns?: ChatAuthBtns[] | ChatOTPBtns[] };
    isHistoryMsg?: boolean;
    convId?: number;
    disableBtn?: boolean;
    resultSize: number;
    needShowMore: boolean;
    isUnfold: boolean;
    updateShowMore: () => void;
    handleHyperLink: (e: React.MouseEvent<HTMLElement>) => void;
    handleHyperLinkKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
    handleHyperLinkTouchEnd: (e: React.TouchEvent<HTMLElement>) => void;
    callDetectIntent: (button: Button) => void;
    dispatch: any;
    date?: Date;
}

interface QuickResponseProps {
    quickResponse: any;
    disableBtn?: boolean;
    resultSize: number;
    needShowMore: boolean;
    isUnfold: boolean;
    updateShowMore: () => void;
    handleHyperLink: (e: React.MouseEvent<HTMLElement>) => void;
    handleHyperLinkKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
    handleHyperLinkTouchEnd: (e: React.TouchEvent<HTMLElement>) => void;
    callDetectIntent: (button: Button) => void;
    date?: Date;
}

const renderFormParameter = (formButtonParam: FormButtonParams, index: number) => {
    if ("input" == formButtonParam.tag) {
        return <input key={index} type={formButtonParam.tagType} name={formButtonParam.tagName} value={formButtonParam.tagValue} />;
    }
    if ("button" == formButtonParam.tag) {
        return <button key={index}>{formButtonParam.tagText}</button>;
    }
    return null;
};

const parseFormButtonValue = (raw?: string): FormButtonValue => {
    return safeJsonParse<FormButtonValue>(raw, {
        formMethod: "GET",
        formId: "",
        formAction: "",
        formTarget: "_self",
        formParameters: []
    });
};

const renderRichAction = (
    richResponse: any,
    actionParam: { type?: string; form?: ActionParamAuth[]; btns?: ChatAuthBtns[] | ChatOTPBtns[] },
    isHistoryMsg?: boolean,
    convId?: number
) => {
    const actionId = richResponse?.action?.id;

    if (actionId === "1002") {
        return <ChatAuthForm
            actionId={richResponse.action.actionCode}
            type={actionParam.type}
            paramForm={actionParam.form}
            isHistoryMsg={isHistoryMsg}
            btns={actionParam.btns}>
        </ChatAuthForm>;
    }

    if (actionId === "1003") {
        return <ChatOtp
            actionId={richResponse.action.actionCode}
            type={actionParam.type}
            btns={actionParam.btns}
            isHistoryMsg={isHistoryMsg}>
        </ChatOtp>;
    }

    if (actionId === "1004") {
        return <ChatFeedback isHistoryMsg={isHistoryMsg} convId={convId} param={richResponse?.action?.param} actionCode={richResponse?.action?.actionCode}>
        </ChatFeedback>;
    }

    if (actionId === "1009") {
        return <NewChatFeedback isHistoryMsg={isHistoryMsg} convId={convId} param={richResponse?.action?.param} actionCode={richResponse?.action?.actionCode}>
        </NewChatFeedback>;
    }

    return null;
};

const RichResponseContent: React.FC<RichResponseProps> = ({
    richResponse,
    actionParam,
    isHistoryMsg,
    convId,
    disableBtn,
    resultSize,
    needShowMore,
    isUnfold,
    updateShowMore,
    handleHyperLink,
    handleHyperLinkKeyDown,
    handleHyperLinkTouchEnd,
    callDetectIntent,
    dispatch,
    date
}) => {
    const visibleButtons = richResponse.buttons.slice(0, resultSize == -1 ? richResponse.buttons.length : resultSize).filter(item => item.text && item.text.trim() != "");

    return (
        <div className={styles.ChatBuddyMsgLess} data-testid="richResponse">
            <div className="chat-buddy-msg">
                {richResponse.html && richResponse.html != '<p></p>\n' &&
                    <div
                        className="buddy-html"
                        data-testid="html-click"
                        role="button"
                        tabIndex={0}
                        onClick={handleHyperLink}
                        onKeyDown={handleHyperLinkKeyDown}
                        onTouchEnd={handleHyperLinkTouchEnd}
                    >
                        {ReactHtmlParser(sanitizeHtml(richResponse.html), htmlParserOptions)}
                    </div>}

                {renderRichAction(richResponse, actionParam, isHistoryMsg, convId)}
                {(richResponse.buttons && richResponse.buttons.length > 0) &&
                    <ul className="buddy-buttons">
                        {visibleButtons.map((elem, index) => {
                            const formButtonValue = elem.type === "form" ? parseFormButtonValue(elem.url) : null;

                            let className = "btn-item";

                            if (elem.styleType === "CTAMain") {
                              className = "btn-cta-main";
                            } else if (elem.styleType === "CTASub") {
                              className = "btn-cta-sub";
                            } else if (disableBtn) {
                              className = "btn-item-disable";
                            }

                            return (
                                <li
                                    key={elem.text} data-testid="buddy-button"
                                    className={className}
                                >
                                    <div hidden={elem.type !== "file"} className="button buttons-file">
                                        {isSafeUrl(elem.url) && (
                                            <button
                                              type="button"
                                              className="file-download-button"
                                              onClick={() =>
                                                dispatch(
                                                  downloadByButtonAction({
                                                    buttonUrl: elem.url,
                                                    fileName: elem.fileName,
                                                  })
                                                )
                                              }
                                            >
                                              {elem.text}
                                            </button>
                                          )}
                                        {!isSafeUrl(elem.url) &&
                                            <button type="button" className="button-a" disabled>{elem.text}</button>}
                                    </div>
                                    <div hidden={elem.type !== "html"} className="button buttons-html">
                                        {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                        <a href={sanitizeUrl(elem.url)} target={getBlankOrSelf(sanitizeUrl(elem.url), "_blank")}
                                            rel="noopener noreferrer">{elem.text}</a>
                                    </div>
                                    <div hidden={elem.type !== "form"} className="button buttons-html">
                                        {elem.type == "form" && formButtonValue &&
                                            <form key={formButtonValue.formId} method={sanitizeFormMethod(formButtonValue.formMethod)} id={formButtonValue.formId} action={sanitizeUrl(formButtonValue.formAction, "")} target={sanitizeFormTarget(formButtonValue.formTarget)}>
                                                {formButtonValue.formParameters.map((formButtonParam: FormButtonParams, itemIndex: number) => renderFormParameter(formButtonParam, itemIndex))}
                                            </form>
                                        }
                                    </div>
                                    <div hidden={elem.type !== "openURL"} className="button">
                                        {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                        <a href={sanitizeUrl(elem.url)} target={"_top"}
                                            rel="noopener noreferrer">{elem.text}</a>
                                    </div>
                                    {(elem.type === "file" || elem.type === "html" || elem.type === "openURL" || elem.type === "form") ? "" :
                                        <button type="button" className="button-a" onClick={() => callDetectIntent(elem)}>{elem.text}</button>}
                                </li>
                            );
                        })}
                        <li
                            data-testid="unfold-click"
                        >
                            <div hidden={!needShowMore} className={disableBtn ? "btn-item-disable" : "btn-item"}>
                                <button
                                    type="button"
                                    className="button-a"
                                    onClick={updateShowMore}
                                    aria-label={isUnfold ? "Show less options" : "Show more options"}
                                >
                                    <img src={isUnfold ? foldImg : unfoldImg} alt="" />
                                </button>
                            </div>
                        </li>
                    </ul>
                }
            </div>
            <ChatTime date={date}></ChatTime>
        </div>
    );
};

const QuickResponseContent: React.FC<QuickResponseProps> = ({
    quickResponse,
    disableBtn,
    resultSize,
    needShowMore,
    isUnfold,
    updateShowMore,
    handleHyperLink,
    handleHyperLinkKeyDown,
    handleHyperLinkTouchEnd,
    callDetectIntent,
    date
}) => {
    const visibleButtons = quickResponse.buttons.slice(0, resultSize == -1 ? quickResponse.buttons.length : resultSize).filter(item => item.text && item.text.trim() != "");

    return (
        <div className={styles.ChatBuddyMsgLess} data-testid="quickResponse">
            <div className="chat-buddy-msg">
                {quickResponse.text && <div className="buddy-html" data-testid="html-click" role="button" tabIndex={0} onClick={handleHyperLink} onKeyDown={handleHyperLinkKeyDown} onTouchEnd={handleHyperLinkTouchEnd}>
                                                    {ReactHtmlParser(sanitizeHtml(quickResponse.text), htmlParserOptions)}
                                                  </div>}
                {(quickResponse.buttons && quickResponse.buttons.length > 0) &&
                    <ul className="buddy-buttons">
                        {visibleButtons.map((elem) => {
                            return (
                                <li
                                    key={elem.text}
                                    className={disableBtn ? "btn-item-disable" : "btn-item"}
                                >
                                    <button type="button" className="button-a" onClick={() => callDetectIntent(elem)}>{elem.text}</button>
                                </li>
                            );
                        })}
                        <li>
                            <div hidden={!needShowMore} className={disableBtn ? "btn-item-disable" : "btn-item"}>
                                <button
                                    type="button"
                                    className="button-a"
                                    onClick={updateShowMore}
                                    aria-label={isUnfold ? "Show less options" : "Show more options"}
                                >
                                    <img src={isUnfold ? foldImg : unfoldImg} alt="" />
                                </button>
                            </div>
                        </li>
                    </ul>
                }
            </div>
            <ChatTime date={date}></ChatTime>
        </div>
    );
};

const FallbackResponseContent: React.FC<{ date?: Date }> = ({ date }) => (
    <div className={styles.ChatBuddyMsgLess}>
        <div className="chat-buddy-msg">
            maybe something goes wrong, contact us.Thank you for your coorperation!
        </div>
        <ChatTime date={date}></ChatTime>
    </div>
);

const ChatUserMsg: React.FC<BuddyMsgProps> = (props: BuddyMsgProps) => {
    const message = props.buddyMsg as DetectIntentData;
    const isHistoryMsg = props.isHistoryMsg;
    const disableBtn = props.disableBtn;
    const fulfillmentMessages = message.fulfillmentMessages[0];
    const richResponse = fulfillmentMessages.richResponse,
        quickResponse = fulfillmentMessages.quickResponses;
    const dispatch = useDispatch();
    const [resultSize, setResultSize] = useState(5);
    const [needShowMore, setNeedShowMore] = useState(false);
    const [isUnfold, setIsUnfold] = useState(false);
    const actionParam = safeJsonParse<{ type?: string; form?: ActionParamAuth[]; btns?: ChatAuthBtns[] | ChatOTPBtns[] }>(richResponse?.action?.param, {});

    const handleHyperLinkTarget = (target: EventTarget | null) => {
        const htmlTarget = target as HTMLElement | null;
        if (htmlTarget?.dataset?.click && htmlTarget.dataset.msg) {
            let dataStr: string = htmlTarget.dataset.click;
            let dataMsg: string = htmlTarget.dataset.msg;
            let pattern = /^query-string-\S*/;
            if (pattern.exec(dataStr)) {
                //add a date time
                const userMsg: ChatMsg = { msg: dataMsg, type: USER_MSG, date: new Date() };
                dispatch(addChatMsgAction(userMsg));
                dispatch(sendMessageAction({ message: dataStr, name: dataMsg }));
            }
        }
    }

    const handleHyperLink = (e: React.MouseEvent<HTMLElement>) => {
        handleHyperLinkTarget(e.target);
    }

    const handleHyperLinkKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleHyperLinkTarget(e.target);
        }
    }

    const handleHyperLinkTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
        handleHyperLinkTarget(e.target);
    }

    const callDetectIntent = (button: Button) => {
        if (button.type === "file" || button.type === "html" || button.type === "openURL" || button.type === "form") {
            let userAction = {
                buttonText: button.text,
                buttonUrl: button.url,
                intentCode: message.intentCode
            }
            dispatch(reportUserAction(userAction));
            return;
        }

        if (button.type === "action" && button.action) {
            if (button.action.id == "1000") {
                console.log("trigger frontend action");
                parent.postMessage(JSON.stringify({ action: "minusView", viewUrl: document.URL }), sanitizeTargetOrigin(CONFIG.TARGET_ORIGIN));
                return;
            }
        }

        //add a date time
        const userMsg: ChatMsg = { msg: button.text, type: USER_MSG, date: new Date() };
        dispatch(addChatMsgAction(userMsg));

        const reqMsg = button.postback?.split("/");
        const lastReqMsg = reqMsg && reqMsg.length > 0 ? reqMsg[reqMsg.length - 1] : "";
        dispatch(sendMessageAction({ type: props.intentType, message: lastReqMsg, name: button.text }));
    };

    useEffect(() => {
        if ((richResponse?.buttons?.length > 5) || (quickResponse?.buttons?.length > 5)) {
            setNeedShowMore(true);
            setResultSize(4);
        }
    }, [])

    const updateShowMore = () => {
        if (isUnfold) {
            setResultSize(4);
        } else {
            setResultSize(-1)
        }
        setIsUnfold(!isUnfold);
    }

    if (richResponse) {
        return <RichResponseContent
            richResponse={richResponse}
            actionParam={actionParam}
            isHistoryMsg={isHistoryMsg}
            convId={props.convId}
            disableBtn={disableBtn}
            resultSize={resultSize}
            needShowMore={needShowMore}
            isUnfold={isUnfold}
            updateShowMore={updateShowMore}
            handleHyperLink={handleHyperLink}
            handleHyperLinkKeyDown={handleHyperLinkKeyDown}
            handleHyperLinkTouchEnd={handleHyperLinkTouchEnd}
            callDetectIntent={callDetectIntent}
            dispatch={dispatch}
            date={props.date}
        />;
    }

    if (quickResponse) {
        return <QuickResponseContent
            quickResponse={quickResponse}
            disableBtn={disableBtn}
            resultSize={resultSize}
            needShowMore={needShowMore}
            isUnfold={isUnfold}
            updateShowMore={updateShowMore}
            handleHyperLink={handleHyperLink}
            handleHyperLinkKeyDown={handleHyperLinkKeyDown}
            handleHyperLinkTouchEnd={handleHyperLinkTouchEnd}
            callDetectIntent={callDetectIntent}
            date={props.date}
        />;
    }

    return <FallbackResponseContent date={props.date} />;

}

export default ChatUserMsg;